import { Injectable } from '@angular/core';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { ProductReference } from '../models/layout/product-reference';
import { DragContext } from '../models/drag-context';
import { LayoutUtilitiesService } from '../utilities/layout.utilities';
import { ImageService } from './image.service';
import { ZoomService } from './zoom.service';
import { LayoutService } from './layout.service';
import { PlaceholderService } from './placeholder.service';
import { DialogService } from './dialog.service';
import { ValidationError } from './planogram-validation.service';

@Injectable({ providedIn: 'root' })
export class DragContextService {
  private _context: DragContext = {
    source: null,
    draggedProduct: null,
    draggedShelf: null,
    moved: false,
  };

  private dragPreview: HTMLElement | null = null;
  private mouseMoveHandler: ((event: MouseEvent) => void) | null = null;
  private mouseUpHandler: ((event: MouseEvent) => void) | null = null;

  private pendingStart: {
    productRef: ProductReference;
    startX: number;
    startY: number;
    caseConfig?: number | null;
  } | null = null;

  private readonly dragStartThreshold = 5; // Minimum pixels moved before starting drag

  constructor(
    private layoutUtilities: LayoutUtilitiesService,
    private placeholderService: PlaceholderService,
    private imageService: ImageService,
    private layoutService: LayoutService,
    private zoomService: ZoomService,
    private dialogService: DialogService
  ) {}

  // --- SHELF ------------------------------------------------------

  onMouseDownFromShelfHandle(
    dropIndex: number,
    shelfIndex: number,
    shelfElement: HTMLElement,
    event: MouseEvent
  ): void {
    this.clearContext();
    this.pendingStart = {
      productRef: null as any,
      startX: event.clientX,
      startY: event.clientY,
    };

    const moveHandler = (moveEvent: MouseEvent) => {
      if (this.pendingStart) {
        const dx = Math.abs(moveEvent.clientX - this.pendingStart!.startX);
        const dy = Math.abs(moveEvent.clientY - this.pendingStart!.startY);
        if (dx > this.dragStartThreshold || dy > this.dragStartThreshold) {
          this.startDragFromShelf(
            dropIndex,
            shelfIndex,
            shelfElement,
            moveEvent
          );
          document.removeEventListener('mousemove', moveHandler);
        }
      }
    };

    const upHandler = () => {
      this.pendingStart = null;
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  }

  private startDragFromShelf(
    dropIndex: number,
    shelfIndex: number,
    shelfElement: HTMLElement,
    event: MouseEvent
  ): void {
    const shelfData = this.layoutService.getShelf(dropIndex, shelfIndex);
    if (!shelfData) return;

    this._context = {
      source: {
        type: 'shelf',
        dropIndex,
        shelfIndex,
        stackIndex: null,
        productIndex: null,
        onlyProductInStack: false,
      },
      draggedProduct: null,
      draggedShelf: shelfData,
      moved: false,
    };
    this.pendingStart = null;

    this.createShelfDragPreview(shelfElement, event);

    // 💡 Instead of removing shelf, mark it as dragging
    this.layoutService.setShelfDragging(dropIndex, shelfIndex, true);
  }

  // --- PRODUCT STACK ------------------------------------------------------

  onMouseDownFromStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    productRef: ProductReference,
    product: PlanogramProduct,
    event: MouseEvent
  ): void {
    this.clearContext();
    this.pendingStart = {
      productRef: null as any, // not used for stack drag
      startX: event.clientX,
      startY: event.clientY,
    };

    const moveHandler = (moveEvent: MouseEvent) => {
      if (this.pendingStart) {
        const dx = Math.abs(moveEvent.clientX - this.pendingStart.startX);
        const dy = Math.abs(moveEvent.clientY - this.pendingStart.startY);

        if (dx > this.dragStartThreshold || dy > this.dragStartThreshold) {
          this.startDragFromStack(
            dropIndex,
            shelfIndex,
            stackIndex,
            productIndex,
            productRef,
            product,
            moveEvent
          );
          document.removeEventListener('mousemove', moveHandler);
        }
      }
    };

    const upHandler = () => {
      this.pendingStart = null;
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  }

  private startDragFromStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number,
    productRef: ProductReference,
    product: PlanogramProduct,
    event: MouseEvent
  ): void {
    let stack = this.layoutService.getStack(dropIndex, shelfIndex, stackIndex);

    this._context = {
      source: {
        type: 'stack',
        dropIndex: dropIndex,
        shelfIndex: shelfIndex,
        stackIndex: stackIndex,
        productIndex: productIndex,
        onlyProductInStack: stack?.products.length == 1,
      },
      draggedProduct: product,
      draggedShelf: null,
      moved: false,
    };

    this.pendingStart = null;

    this.createStackDragPreview(productRef, event);

    // 💡 Instead of removing product, mark as dragging
    this.layoutService.setProductDragging(
      dropIndex,
      shelfIndex,
      stackIndex,
      productIndex,
      true
    );
  }

  // --- PRODUCT BIN -------------------------------------------------------

  /**
   * Called when the user presses the mouse on a product in the product bin.
   * It sets up a pending start operation which will begin only if the user moves
   * more than a small threshold distance (to distinguish between a click and a drag).
   */
  onMouseDownFromProductBin(
    productRef: ProductReference,
    event: MouseEvent,
    caseConfig?: number | null
  ): void {
    this.clearContext();
    this.pendingStart = {
      productRef,
      startX: event.clientX,
      startY: event.clientY,
      caseConfig,
    };

    const moveHandler = (moveEvent: MouseEvent) => {
      if (this.pendingStart) {
        const dx = Math.abs(moveEvent.clientX - this.pendingStart.startX);
        const dy = Math.abs(moveEvent.clientY - this.pendingStart.startY);
        // Initiate drag operation when movement exceeds threshold
        if (dx > this.dragStartThreshold || dy > this.dragStartThreshold) {
          this.startDragFromProductBin(this.pendingStart.productRef, moveEvent, this.pendingStart.caseConfig);
          document.removeEventListener('mousemove', moveHandler);
        }
      }
    };

    const upHandler = () => {
      // Cancel pending drag if released before moving far enough
      this.pendingStart = null;
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  }

  /**
   * Called when the user moves enough distance while holding down the mouse
   * to start a drag operation from the product bin.
   * Creates a temporary PlanogramProduct representation of the product for dragging.
   */
  private startDragFromProductBin(
    productRef: ProductReference,
    event: MouseEvent,
    caseConfig?: number | null
  ): void {
    const product: PlanogramProduct = {
      productId: productRef.productId,
      position: 0,
      vposition: 0,
      stackHeight: 1,
      facingDepth: productRef.dFacings,
      squeeze: 0,
      orientation: productRef.orientation,
      caseConfig: caseConfig ?? null,
    };
    product.dimensions = this.layoutUtilities.calculateEffectiveDimensions(productRef, product);

    this._context = {
      source: {
        type: 'bin',
        dropIndex: null,
        shelfIndex: null,
        stackIndex: null,
        productIndex: null,
        onlyProductInStack: false,
      },
      draggedProduct: product,
      draggedShelf: null,
      moved: false,
    };

    this.pendingStart = null;
    this.createStackDragPreview(productRef, event);
  }

  // --- DRAG PREVIEW HANDLING -------------------------------------------------------

  /**
   * Creates a floating DOM element representing a dragged shelf.
   * This is visually similar to createStackDragPreview but tailored for shelves.
   */
  private createShelfDragPreview(
    shelfElement: HTMLElement,
    event: MouseEvent
  ): void {
    const width = shelfElement.clientWidth;
    const height = shelfElement.clientHeight;

    this.dragPreview = shelfElement.cloneNode(true) as HTMLElement;
    this.dragPreview.classList.add('drag-shelf-preview');
    this.dragPreview.style.width = `${width}px`;
    this.dragPreview.style.height = `${height}px`;

    // Place under mouse
    this.updatePreviewPosition(
      event.clientX - width / 2,
      event.clientY - height / 2
    );
    document.body.appendChild(this.dragPreview);

    this.mouseMoveHandler = (moveEvent: MouseEvent) =>
      this.updatePreviewPosition(
        moveEvent.clientX - width / 2,
        moveEvent.clientY - height / 2
      );
    this.mouseUpHandler = (upEvent: MouseEvent) => this.dropHandler(upEvent);
    this.attachGlobalListeners();
  }

  /**
   * Creates a floating DOM element following the mouse cursor during a drag operation.
   * The preview visually represents the product being dragged.
   */
  private createStackDragPreview(
    productRef: ProductReference,
    event: MouseEvent
  ): void {
    if (!this._context.draggedProduct?.dimensions) return;

    const zoom = this.zoomService.getCurrentZoom();
    const productWidth = this._context.draggedProduct.dimensions.width * zoom;
    const productHeight = this._context.draggedProduct.dimensions.height * zoom;
    const stackHeight = this._context.draggedProduct.stackHeight ?? 1;

    // Create outer preview container
    this.dragPreview = document.createElement('div');
    this.dragPreview.className = 'drag-preview';
    this.dragPreview.style.width = `${productWidth}px`;
    this.dragPreview.style.height = `${productHeight * stackHeight}px`;

    // Create stacked product divs. Tray/case placements use the barcode+'T'/'C' image and fall
    // back to the unit image when the variant image has not been uploaded.
    const caseConfig = this._context.draggedProduct?.caseConfig;
    const variantBarcode =
      caseConfig === 1 ? productRef.barcode + 'T'
      : caseConfig === 2 ? productRef.barcode + 'C'
      : productRef.barcode;
    const imageUrl = this.imageService.getImageUrl(
      variantBarcode,
      productRef.orientation,
      variantBarcode !== productRef.barcode ? productRef.barcode : undefined
    );
    for (let i = 0; i < stackHeight; i++) {
      const item = document.createElement('div');
      item.className = 'drag-preview-item';
      item.style.width = `${productWidth}px`;
      item.style.height = `${productHeight}px`;
      item.style.backgroundImage = `url(${imageUrl})`;
      this.dragPreview.appendChild(item);
    }

    // Center under mouse
    this.updatePreviewPosition(
      event.clientX - productWidth / 2,
      event.clientY - (productHeight * stackHeight) / 2
    );
    document.body.appendChild(this.dragPreview);

    // Track movement / release
    this.mouseMoveHandler = (moveEvent: MouseEvent) => {
      this.updatePreviewPosition(
        moveEvent.clientX - this.dragPreview!.clientWidth / 2,
        moveEvent.clientY - this.dragPreview!.clientHeight / 2
      );
    };
    this.mouseUpHandler = (upEvent: MouseEvent) => this.dropHandler(upEvent);
    this.attachGlobalListeners();
  }

  /**
   * Called when the user releases the mouse to end a drag operation.
   */
  private dropHandler(event: MouseEvent): void {
  const placeholder = this.placeholderService.getCurrentPlaceholder();

  try {
    if (!placeholder) return;

    const src = this._context.source;

    if (placeholder.type == null) {
      // if dropped on the product bin (no drop/shelf target)
      if (src?.type === 'stack' && this._context.draggedProduct) {
        const { dropIndex, shelfIndex, stackIndex, productIndex } = src;
        if (
          dropIndex !== null &&
          shelfIndex !== null &&
          stackIndex !== null &&
          productIndex !== null
        ) {
          this.layoutService.removeProduct(dropIndex, shelfIndex, stackIndex, productIndex);
          this._context.moved = true;
        }
      }

      // No valid drop target — just clear the drag state
      this.clearContext();
      this.placeholderService.clearPlaceholder();
      return;
    }

    // 🟢 Add to floating shelf ---------------------------------------
    // Dropped onto the floating shelf tray. Additive + de-duplicated by
    // product id — the product stays wherever it already is in the layout.
    if (placeholder.type === 'floating-shelf') {
      if (this._context.draggedProduct) {
        this.layoutService.addProductToFloatingShelf(
          this._context.draggedProduct.productId
        );
        this._context.moved = true;
      }
      this.clearContext();
      this.placeholderService.clearPlaceholder();
      return;
    }

     // 🟢 Move product -------------------------------------------------
    if (this._context.draggedProduct && src?.type === 'stack') {
      const srcDrop = src.dropIndex;
      const srcShelf = src.shelfIndex;
      const srcStack = src.stackIndex;
      const srcProd = src.productIndex;

      const targetDrop = placeholder.dropIndex;
      const targetShelf = placeholder.shelfIndex;
      let targetStack = placeholder.stackIndex;
      const targetProductIndex = placeholder.position;

      if (
        srcDrop !== null &&
        srcShelf !== null &&
        srcStack !== null &&
        srcProd !== null &&
        targetDrop !== null &&
        targetShelf !== null
      ) {
        const placingInExistingStack = placeholder.type === 'product';
        
        this.layoutService.moveProduct(
          srcDrop,
          srcShelf,
          srcStack,
          srcProd,
          targetDrop,
          targetShelf,
          targetStack ?? 0,
          placingInExistingStack ? false : true,
          placingInExistingStack ? targetProductIndex ?? 0 : 0
        );

        this._context.moved = true;
      }
    }
    // 🟢 Move shelf ---------------------------------------------------
    else if (
      this._context.draggedShelf &&
      src?.type === 'shelf' &&
      src.dropIndex !== null &&
      src.shelfIndex !== null &&
      placeholder.dropIndex !== null &&
      placeholder.shelfIndex !== null
    ) {
      this.layoutService.moveShelf(
        src.dropIndex,
        src.shelfIndex,
        placeholder.dropIndex,
        placeholder.shelfIndex
      );
      this._context.moved = true;
    }
    else if (this._context.draggedProduct && src?.type == 'bin') {
      const product = this._context.draggedProduct;
      if (!product || !placeholder) return;

      const { dropIndex, shelfIndex, stackIndex, position, type } = placeholder;
      if (dropIndex === null || shelfIndex === null) return;

      // Determine target stack scenario
      const placingInExistingStack = type === 'product';

      try {
        if (placingInExistingStack && stackIndex !== null) {
          // Drop into an existing stack
          this.layoutService.addProductToStack(
            dropIndex,
            shelfIndex,
            stackIndex,
            position ?? 0,
            product
          );
        } else {
          // Drop into a new stack on the shelf
          this.layoutService.addProductAsNewStack(
            dropIndex,
            shelfIndex,
            stackIndex ?? 0,
            product
          );
        }
        this._context.moved = true;
      } catch (e) {
        console.error('Error adding product from bin', e);
        if (e instanceof ValidationError) {
          this.dialogService.showError(e.message, 'Validation Error');
        }
      }
    }

  } catch (e) {
    console.error(e);
    if (e instanceof ValidationError) {
      this.dialogService.showError(e.message, 'Validation Error');
    }
  }

  // Always clear drag state
  this.clearContext();
  this.placeholderService.clearPlaceholder();
}

  /**
   * Updates the on-screen position of the floating drag preview element.
   */
  private updatePreviewPosition(x: number, y: number): void {
    if (this.dragPreview) {
      this.dragPreview.style.transform = `translate(${x}px, ${y}px)`;
    }
  }

  /**
   * Returns true if a product is currently being dragged.
   */
  isDraggingProduct(): boolean {
    return this._context.draggedProduct !== null;
  }

  /**
   * Returns true if a shelf is currently being dragged.
   */
  isDraggingShelf(): boolean {
    return this._context.draggedShelf !== null;
  }

  /**
   * Returns true if a product is currently being dragged.
   */
  getContext(): DragContext {
    return this._context;
  }

  /**
   * Resets all drag-related state and removes any active preview element.
   */
  clearContext(): void {
    this.cleanupDragPreview();
    this.pendingStart = null;

    //If at this point the product is still in drag context, then it was not moved to different position.
    if (
      this._context.draggedProduct &&
      this._context.source &&
      !this._context.moved
    ) {
      let source = this._context.source;
      if (
        source.type == 'stack' &&
        source.dropIndex !== null &&
        source.shelfIndex !== null &&
        source.stackIndex !== null &&
        source.productIndex !== null
      ) {
        this.layoutService.setProductDragging(source.dropIndex!, source.shelfIndex!, source.stackIndex!, source.productIndex!, false);
      }
    }
       else if (
      this._context.draggedShelf &&
      this._context.source &&
      !this._context.moved
    ) {
      let source = this._context.source;
      if (
        source.type == 'shelf' &&
        source.dropIndex !== null &&
        source.shelfIndex !== null
      ) {
        this.layoutService.setShelfDragging(source.dropIndex!, source.shelfIndex!, false);
      }
    }

    this._context = {
      source: null,
      draggedProduct: null,
      draggedShelf: null,
      moved: false,
    };
  }

  // --- Centralized mouse binding helpers ------------------------------------------

  /**
   * Binds global mouse event listeners for drag movement and release.
   */
  private attachGlobalListeners(): void {
    if (this.mouseMoveHandler)
      document.addEventListener('mousemove', this.mouseMoveHandler);
    if (this.mouseUpHandler)
      document.addEventListener('mouseup', this.mouseUpHandler);
  }

  /**
   * Removes global mouse event listeners to avoid leaks or unintended behavior.
   */
  private detachGlobalListeners(): void {
    if (this.mouseMoveHandler)
      document.removeEventListener('mousemove', this.mouseMoveHandler);
    if (this.mouseUpHandler)
      document.removeEventListener('mouseup', this.mouseUpHandler);
  }

  /**
   * Cleans up the visual drag preview and removes any event listeners associated with it.
   */
  private cleanupDragPreview(): void {
    if (this.dragPreview) {
      document.body.removeChild(this.dragPreview);
      this.dragPreview = null;
    }

    this.detachGlobalListeners();
    this.mouseMoveHandler = null;
    this.mouseUpHandler = null;
  }
}
